import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getProcedureRevisionById,
  updateProcedureRevision,
  getProcedureRevisionsForPart,
} from "../../redux/procedureRevisionSlice";
import { showSnackbar } from "../../redux/snackbarSlice";
import { openPreview } from "../../redux/previewSlice";
import AppBar from "../../common/AppBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import Loader from "../../common/Loader";
import "./EditProcedureRevision.css";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker.js";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import { getAllVersionHistories } from "../../redux/versionHistorySlice";
import VersionHistoryPopup from "../../common/VersionHistoryPopup/VersionHistoryPopup";
import { buildPermissionContext } from "../../util/permissions";
import { setTaskReference } from "../Tasks/Uitlities";

const EditProcedureRevision = () => {
  const { revisionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedText, setLastSavedText] = useState("");
  const [formData, setFormData] = useState({
    procedure_number: "",
    procedure_type: "",
    references: "",
    description: "",
    revision_details: "",
    rev: "",
    training_not_required: false,
    hide_consolidated_bom: false,
    version_id_ref: null,
  });

  // Key to persist draft form state across navigation to Preview and back
  const draftKey = `editRevisionDraft_${revisionId}`;
  // Track initial form data for comparison
  const [initialFormData, setInitialFormData] = useState(null);
  const hasRestoredDraft = useRef(false);
  const breadcrumb_text = `${formData.procedure_number} > Rev ${formData.rev}`;
  const pageTitle =
    `${formData.procedure_type} ${formData.procedure_number}, Rev ${formData.rev}` ||
    "Edit Document Revision";

  const versionHistoryPopupTitle = useMemo(() => {
    const pn =
      formData.procedure_number != null
        ? String(formData.procedure_number).trim()
        : "";
    const rev =
      formData.rev != null ? String(formData.rev).trim() : "";
    if (pn && rev) return `Version History for ${pn} Rev ${rev}`;
    if (pn) return `Version History for ${pn}`;
    return "Version History";
  }, [formData.procedure_number, formData.rev]);
  // Back navigation handling with shared blocker
  const handleAppBarBack = useCallback(() => {
    // Let the useUnsavedChangesBlocker handle the confirmation
    // Use window.history.back() to trigger the useUnsavedChangesBlocker
    window.history.back();
  }, []);
  const [apiIds, setApiIds] = useState({});
  /** Procedure root tree node (from API) for Node History filtering in version content */
  const [procedureTreeNodeId, setProcedureTreeNodeId] = useState("");

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Get version histories from Redux
  const { versionHistories, loading: versionHistoryLoading } = useSelector(
    (state) => state.versionHistory,
  );

  useUnsavedChangesBlocker(hasChanges && !saving);

  useEffect(() => {
    // Reset restoration flag on mount
    hasRestoredDraft.current = false;

    const fetchRevisionData = async () => {
      try {
        // Force fresh API call by adding timestamp to bypass any potential caching
        const response = await dispatch(
          getProcedureRevisionById(revisionId)
        ).unwrap();
        const { results } = response;

        setApiIds({
          partId: results.part_ref,
          revisionId: revisionId,
        });
        setProcedureTreeNodeId(results?.tree_node_details?.tree_node_id || "");

        const newFormData = {
          procedure_number: results.part_data?.part_number || "",
          procedure_type: results.part_data?.type?.name || "",
          references: results.reference || "",
          description: results.part_data?.description || "",
          revision_details: results.revision_details || "",
          training_not_required: results.training_not_required || false,
          hide_consolidated_bom: results.hide_consolidated_bom || false,
          version_id_ref: results.version_id_ref || null,
          rev: results.rev || "",
        };

        // First, set the initial form data
        setInitialFormData(newFormData);

        // Check if coming from navigation (not preview return) - clear draft
        const isFromPreview = location.state?.fromPreview;

        if (!isFromPreview) {
          // Clear any old draft data when opening fresh (not from preview)
          sessionStorage.removeItem(draftKey);
          setFormData(newFormData);
        } else {
          // Restore draft only if returning from preview
          try {
            const draftJson = sessionStorage.getItem(draftKey);
            if (draftJson && !hasRestoredDraft.current) {
              const draft = JSON.parse(draftJson);
              if (draft && typeof draft === "object") {
                // Merge draft with newFormData to ensure all fields are present
                const mergedFormData = { ...newFormData, ...draft };
                setFormData(mergedFormData);
                setHasChanges(true);
                hasRestoredDraft.current = true;
              } else {
                setFormData(newFormData);
              }
            } else {
              setFormData(newFormData);
            }
          } catch (error) {
            console.error("Error parsing draft:", error);
            setFormData(newFormData);
          }
        }

        // Set task reference for this revision
        setTaskReference(
          dispatch,
          // `${results.part_data?.part_number || ""} Rev ${results.rev || ""}`,
          `Rev ${results.rev || ""} , ${results.part_data?.part_number || ""}`,
          results.procedure_revision_id || revisionId,
          results?.tree_node_details?.tree_node_id,
          results?.tree_node_details.ltree_path,
          true
        );

        // Get the timestamp from updated_at or created_at
        const timestamp = results.updated_at || results.created_at;

        // Get user info from status_changes if available
        let userName = "Unknown User";
        if (results.status_changes && results.status_changes.length > 0) {
          const latestStatus = results.status_changes.find(
            (status) => status.is_latest === true
          );
          if (
            latestStatus &&
            latestStatus.audit_trail &&
            latestStatus.audit_trail.user
          ) {
            const { first_name, last_name } = latestStatus.audit_trail.user;
            userName = `${first_name} ${last_name}`
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");
          }
        }

        if (timestamp) {
          const date = new Date(timestamp);

          // Convert to EST manually by shifting UTC offset
          const { generateLastSavedText } = require("../../util/stepUtils");
          const finalString = generateLastSavedText(timestamp, userName);
          setLastSavedText(finalString);
        }
      } catch (error) {
        // Error fetching revision data - no snackbar
      } finally {
        setLoading(false);
      }
    };

    fetchRevisionData();
  }, [dispatch, revisionId]);

  // Fetch version history when popup opens
  useEffect(() => {
    if (showVersionHistory && formData.version_id_ref) {
      dispatch(getAllVersionHistories({ versionId: formData.version_id_ref }));
    }
  }, [showVersionHistory, formData.version_id_ref, dispatch]);

  // Refetch data when page becomes visible (e.g., returning from another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Refetch latest data when user returns to this page
        const refetchData = async () => {
          try {
            const response = await dispatch(
              getProcedureRevisionById(revisionId)
            ).unwrap();
            const { results } = response;

            // Only update the initial data, not formData (to preserve user's edits)
            const freshInitialData = {
              procedure_number: results.part_data?.part_number || "",
              procedure_type: results.part_data?.type?.name || "",
              references: results.reference || "",
              description: results.part_data?.description || "",
              revision_details: results.revision_details || "",
              training_not_required: results.training_not_required || false,
              hide_consolidated_bom: results.hide_consolidated_bom || false,
              version_id_ref: results.version_id_ref || null,
              rev: results.rev || "",
            };
            setInitialFormData(freshInitialData);
          } catch (error) {
            console.error("Error refetching data:", error);
          }
        };
        refetchData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, revisionId]);

  // Check for changes whenever formData changes
  useEffect(() => {
    if (initialFormData) {
      const hasFormChanges =
        JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setHasChanges(hasFormChanges);
    }
  }, [formData, initialFormData]);

  // Save draft data to sessionStorage whenever formData changes
  useEffect(() => {
    // Save draft data whenever formData changes (not just when hasChanges is true)
    // This ensures we capture changes immediately
    if (initialFormData && formData) {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify(formData));
      } catch (error) {
        console.error("Error saving draft data:", error);
      }
    }
  }, [formData, initialFormData, draftKey]);

  // Additional effect to ensure hasChanges is set correctly after restoration
  useEffect(() => {
    if (initialFormData && formData) {
      const hasFormChanges =
        JSON.stringify(formData) !== JSON.stringify(initialFormData);
      if (hasFormChanges && !hasChanges) {
        setHasChanges(true);
      }
    }
  }, [formData, initialFormData, hasChanges]);

  // Save draft data when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Component is unmounting, save current form data
      if (initialFormData && formData) {
        try {
          sessionStorage.setItem(draftKey, JSON.stringify(formData));
        } catch (error) {
          console.error("Error saving draft on unmount:", error);
        }
      }
    };
  }, [formData, initialFormData, draftKey]);

  const handleSubmit = async (e) => {
    // e.preventDefault();
    setSaving(true);
    try {
      // Get the current revision data
      const currentRevision = await dispatch(
        getProcedureRevisionById(revisionId)
      ).unwrap();
      const { results } = currentRevision;
      const audit_trail_data = {
        action: "UPDATE_PROCEDURE_REVISION",
        comment: `Update Procedure Revision ${formData.procedure_number} Rev ${formData.rev}`,
        parent_version: formData.version_id_ref,
        version_content: {
          training_not_required: formData.training_not_required,
          hide_consolidated_bom: formData.hide_consolidated_bom,
          procedure_number: formData.procedure_number,
          procedure_type: formData.procedure_type,
          procedure_description: formData.description,
          revision: formData.rev,
          has_audit_trail_preview: true,
          procedure_revision_id: revisionId,
          action_to_show: `Updated ${formData.procedure_number} Rev ${formData.rev}`,
          reference: formData.references,
          revision_details: formData.revision_details,
          breadcrumb_text,
          pageTitle,
        },
      };
      const updatePayload = {
        rev: results.rev,
        date_released: results.date_released,
        revision_details: formData.revision_details,
        reference: formData.references,
        is_maintained_outside_system: results.is_maintained_outside_system,
        part_ref: results.part_ref,
        based_on_version_ref: results.based_on_version_ref,
      };

      console.log("Sending payload:", updatePayload); // Debug log

      const response = await dispatch(
        updateProcedureRevision({
          id: revisionId,
          data: updatePayload,
          audit_trail_data,
        })
      ).unwrap();

      // Refresh the procedure revisions data
      await dispatch(
        getProcedureRevisionsForPart({ partId: results.part_ref })
      );

      dispatch(
        showSnackbar({
          message: "Revision updated successfully",
          severity: "success",
        })
      );
      // Clear any stored draft after successful save
      try {
        sessionStorage.removeItem(draftKey);
      } catch { }
      navigate(-1);
    } catch (error) {
      console.error("Error updating revision:", error);
      dispatch(
        showSnackbar({ message: "Error updating revision", severity: "error" })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    // Handle opening modal if needed
  };

  if (loading) {
    return <Loader text="Loading revision data..." />;
  }
  return (
    <div>
      {saving && <Loader text="Saving revision..." />}
      <AppBar
        onBack={handleAppBarBack}
        showBlackTitle={true}
        subheading={breadcrumb_text}
        title={pageTitle}
        showTablesDropdown={true}
        buttons={[
          {
            label: "Preview",
            onClick: () => {
              // Persist current unsaved changes so they survive closing preview
              try {
                sessionStorage.setItem(draftKey, JSON.stringify(formData));
              } catch { }

              // Open preview with fresh data fetch (no cache)
              // Preview will load revision table, training logs, BOM table, and all nodes
              // Include draft revision data to show unsaved changes in revision table
              dispatch(
                openPreview({
                  partId: apiIds.partId,
                  revisionId: apiIds.revisionId,
                  draftRevision: {
                    reference: formData.references, // Map references to reference for RevisionTable
                    revision_details: formData.revision_details,
                  },
                })
              );
            },
          },
          {
            label: "Save",
            onClick: canEdit ? handleSubmit : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save revisions."
              : undefined,
          },
        ]}
        subheadingAfterTitle={lastSavedText}
        versionHistoryHandler={() => setShowVersionHistory(true)}
      />

      <form className="procedure-form" style={{ marginTop: "-30px" }}>
        {/* <div className="checkbox-container">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.training_not_required}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  training_not_required: e.target.checked,
                })
              }
            />
            Training Not Required
          </label>
          <label className="checkbox-label" style={{ marginLeft: "-10px" }}>
            <input
              type="checkbox"
              checked={formData.hide_consolidated_bom}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  hide_consolidated_bom: e.target.checked,
                })
              }
            />
            Hide Consolidated BOM Table
          </label>
        </div> */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Document Number
              <span
                style={{ marginLeft: "-1px", fontSize: "15px" }}
                className="required"
              >
                *
              </span>{" "}
              (Read Only)
            </label>
            <div className="input-with-icon">
              <input
                type="text"
                style={{
                  borderRadius: "5px 0px 0px 5px",
                  borderRight: "none",
                  backgroundColor: "white",
                  color: "#999999",
                }}
                value={formData.procedure_number}
                placeholder="Enter Document Number"
                readOnly
              // disabled={true}
              />
              <button
                type="button"
                style={{
                  borderRight: "1px solid rgb(215, 215, 215)",
                  borderTop: "1px solid rgb(215, 215, 215)",
                  borderBottom: "1px solid rgb(215, 215, 215)",
                  borderLeft: "none",
                  borderRadius: "0px 5px 5px 0px",
                  backgroundColor: "#f4f5f7",
                }}
                className=""
                onClick={handleOpenModal}
              >
                <FontAwesomeIcon
                  style={{
                    color: "#86b7fe", // Light gray color for disabled look
                    paddingTop: "7.6px",
                    paddingBottom: "7.6px",
                    paddingLeft: "7px",
                    paddingRight: "7px",
                    pointerEvents: "none", // Disable pointer events
                  }}
                  icon={faPencil}
                />
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>
              Document Type
              <span
                style={{ marginLeft: "-1px", fontSize: "15px" }}
                className="required"
              >
                *
              </span>{" "}
              (Read Only)
            </label>
            <input
              type="text"
              style={{ color: "#999999" }}
              value={formData.procedure_type}
              readOnly
            />
          </div>
          <div className="form-group">
            <label>References (Separate Each Reference using Space Key)</label>
            <input
              type="text"
              placeholder="Enter References"
              value={formData.references}
              onChange={(e) => {
                setFormData({ ...formData, references: e.target.value });
                setHasChanges(true);
              }}
            />
          </div>
        </div>

        <div className="form-row" style={{ marginTop: "-30px" }}>
          <div className="form-group">
            <label>Document Description (Read Only)</label>
            <div style={{ width: "92%" }}>
              <RenderHtml
                descriptionBox={true}
                content={formData.description || ""}
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              Revision Details
              <span
                style={{ marginLeft: "-1px", fontSize: "15px" }}
                className="required"
              >
                *
              </span>
            </label>
            <textarea
              placeholder="Enter Revision Details"
              value={formData.revision_details}
              onChange={(e) => {
                setFormData({ ...formData, revision_details: e.target.value });
                setHasChanges(true);
              }}
              style={{}}
            ></textarea>
          </div>
        </div>
      </form>

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          title={versionHistoryPopupTitle}
          versionHistories={versionHistories}
          mode="edit"
          versionId={formData.version_id_ref}
          breadcrumbText={breadcrumb_text}
          revisionTreeNodes={[]}
          nodeId={procedureTreeNodeId}
          nodeIdField="tree_node_id"
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision
          initialFilterType="all"
          historyFilterDropdownDisabled
          emptyMessage="No version history available"
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref;
            if (!aid) return;
            setShowVersionHistory(false);
            navigate(`/audit-trail/move-preview/${aid}`);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryLoading}
        />
      )}
    </div>
  );
};

export default EditProcedureRevision;
